import React, { useEffect, useState } from "react";
import {
  HomeIcon,
  InboxStackIcon,
  InboxArrowDownIcon,
  DocumentDuplicateIcon,
} from "@heroicons/react/24/outline";
import useAuthStore from "../store/auth";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Banner from "../components/Banner";
import { useQuery } from "react-query";
import { apollo } from "../utils/apollo";
import { GET_ME } from "../utils/graphql";
import useIntegration from "../utils/useIntegration";
import Sidebar from "../components/Sidebar";
import TopBar from "../components/TopBar";
import echo from "../utils/echo";
import { toast } from "react-toastify";
function DashboardLayout({ children }) {
  const { token, user, company, roles, clearToken, setUser } = useAuthStore();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { integration } = useIntegration();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  useQuery(
    ["account_details", company?.companyUuid, user?.id],
    async () => {
      const { data } = await apollo.query({
        query: GET_ME,
        fetchPolicy: "network-only",
        variables: {},
      });
      return data?.me;
    },
    {
      keepPreviousData: true,
      refetchOnWindowFocus: true,
      staleTime: 300000,
      onSuccess: (data) => {
        setUser(data);
        if (!data?.active) {
          clearToken();
          window.location.href = process.env.REACT_APP_GAID_URL + `/logout`;
        }
      },
      onError: () => {
        clearToken();
        window.location.href = process.env.REACT_APP_GAID_URL + `/logout`;
      },
    },
  );
  useEffect(() => {
    if (!token) navigate("/login");
  }, [token, navigate]);

  useEffect(() => {
    if (user?.id && echo) {
      const channel = echo.private(`App.Models.User.${user.id}`);
      channel.notification((notification) => {
        if (
          notification.type === "App\\Notifications\\FileImportCompleted" ||
          notification.file_import_uuid
        ) {
          toast.info(notification.message || t("Import completed"), {
            onClick: () => navigate("/policy/import"),
          });
        }
      });

      return () => {
        echo.leave(`App.Models.User.${user.id}`);
      };
    }
  }, [user?.id, navigate, t]);

  // Build navigation dynamically
  const baseNavigation = [
    {
      name: t("navigation.dashboard"),
      href: "/dashboard",
      icon: HomeIcon,
    },
  ];
  const isShafafiya = integration?.integrationType === "shafafiya";
  let navigation = [...baseNavigation];
  if (company?.companyType === "click2secure_validation_company") {
    navigation.push({
      name: "Click2Secure Validation",
      href: "/validation",
      icon: DocumentDuplicateIcon,
    });
  } else {
    navigation.push(
      {
        name: isShafafiya ? t("navigation.members") : t("navigation.policies"),
        href: "/policies",
        icon: InboxStackIcon,
      },
      {
        name: isShafafiya
          ? t("navigation.import_members")
          : t("navigation.import_policies"),
        href: "/policy/import",
        icon: InboxArrowDownIcon,
      },
      {
        name: t("navigation.reports"),
        href: "/reports",
        icon: DocumentDuplicateIcon,
      },
    );
  }
  if (user?.userIsAdmin === true) {
    navigation.push({
      name: t("Activity history"),
      href: "/activity",
      icon: DocumentDuplicateIcon,
    });
  }
  const company_navigation = []; // Placeholder for future use
  const userNavigation = [
    {
      name: "Your profile",
      href: "/profile",
    },
    {
      name: "Sign out",
      href: "/logout",
    },
  ];
  return (
    <div className="min-h-screen bg-gray-50 font-sans">
      <Sidebar
        open={sidebarOpen}
        setOpen={setSidebarOpen}
        navigation={navigation}
        company={company}
        roles={roles}
        t={t}
        company_navigation={company_navigation}
      />

      <div className="lg:ps-72 flex flex-col min-h-screen">
        <TopBar
          setSidebarOpen={setSidebarOpen}
          company={company}
          user={user}
          userNavigation={userNavigation}
          integration={integration}
        />

        {!company?.active && roles?.includes("company") && <Banner />}

        <main className="flex-1 py-6 sm:py-10 px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-7xl">
            <div className="bg-white px-4 py-8 rounded-xl shadow-sm border border-gray-100 min-h-[calc(100vh-12rem)]">
              {children}
            </div>
          </div>
        </main>

        <footer className="py-4 text-center text-xs text-gray-400">
          &copy; {new Date().getFullYear()}
          {t("Linkzdata. All rights reserved.")}
        </footer>
      </div>
    </div>
  );
}
export default DashboardLayout;
